import { NavLink } from 'react-router-dom';
import { useState } from 'react';
import {
  LayoutDashboard,
  Bell,
  Users,
  Brush,
  LifeBuoy,
  Settings,
  ChevronDown,
  ChevronRight,
  Layers,
  Zap,
  AlertTriangle,
  Sparkles,
  Mail,
  Cloud,
  KeyRound,
  Boxes,
  HardDrive,
  type LucideIcon,
} from 'lucide-react';
import tyrlLogo from '../../assets/tyrl-logo.svg';

type Item = { to: string; label: string; icon: LucideIcon; color?: string };

const TOP: Item[] = [
  { to: '/hub', label: 'Overview', icon: LayoutDashboard },
  { to: '/hub/findings', label: 'Findings & Alerts', icon: Bell, color: 'text-sunrise' },
  { to: '/hub/users-permissions', label: 'Users & Permissions', icon: Users, color: 'text-adopt' },
  { to: '/hub/data-health', label: 'Data Health', icon: Brush, color: 'text-design' },
];

// Match the internal Audit sidebar's icon + color choices so consultants and
// client admins share the same visual mapping between audit category and
// pillar. See client/src/components/layout/Sidebar.tsx for the source.
const AUDIT: Item[] = [
  { to: '/hub/audit/objects', label: 'Objects', icon: Layers, color: 'text-design' },
  { to: '/hub/audit/flows', label: 'Flows', icon: Zap, color: 'text-deliver' },
  { to: '/hub/audit/tech-debt', label: 'Tech Debt', icon: AlertTriangle, color: 'text-sunrise' },
  { to: '/hub/audit/foundations', label: 'Foundations', icon: Sparkles, color: 'text-grow' },
  { to: '/hub/audit/email', label: 'Email', icon: Mail, color: 'text-sunset' },
  { to: '/hub/audit/data-cloud', label: 'Data Cloud', icon: Cloud, color: 'text-adopt' },
  { to: '/hub/audit/licenses', label: 'Licenses', icon: KeyRound, color: 'text-adopt' },
  { to: '/hub/audit/apps', label: 'Apps', icon: Boxes, color: 'text-design' },
  { to: '/hub/audit/storage', label: 'Storage', icon: HardDrive, color: 'text-sunrise' },
];

const BOTTOM: Item[] = [
  { to: '/hub/get-help', label: 'Get Help', icon: LifeBuoy },
  { to: '/hub/settings', label: 'Settings', icon: Settings },
];

export function HubSidebar() {
  const [auditOpen, setAuditOpen] = useState(false);

  return (
    <aside className="w-64 shrink-0 border-r border-border-subtle bg-surface-0 text-text flex flex-col">
      <div className="px-5 pt-6 pb-5 border-b border-border-subtle">
        <img src={tyrlLogo} alt="TYRL" className="h-5 mb-3 opacity-90 brightness-0 invert" />
        <div className="text-sm font-semibold text-text-strong tracking-tight">OrgIQ Hub</div>
        <div className="text-[11px] text-text-muted mt-0.5">Salesforce operations</div>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 flex flex-col gap-1">
        <SectionLabel>Workspace</SectionLabel>
        {TOP.map((item) => (
          <SidebarLink key={item.to} {...item} />
        ))}

        <button
          type="button"
          className="mt-2 mx-2 px-3 py-2 rounded-md text-sm flex items-center gap-2 text-text-muted hover:text-text hover:bg-surface-2 transition-colors"
          onClick={() => setAuditOpen((o) => !o)}
          aria-expanded={auditOpen}
        >
          {auditOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="text-[11px] font-semibold uppercase tracking-wider">Audit Detail</span>
        </button>
        {auditOpen && (
          <div className="flex flex-col gap-0.5">
            {AUDIT.map((item) => (
              <SidebarLink key={item.to} {...item} nested />
            ))}
          </div>
        )}

        <div className="mt-auto pt-3 border-t border-border-subtle flex flex-col gap-1">
          <SectionLabel>Support</SectionLabel>
          {BOTTOM.map((item) => (
            <SidebarLink key={item.to} {...item} />
          ))}
        </div>
      </nav>

      <div className="px-5 py-3 border-t border-border-subtle text-[10px] font-medium uppercase tracking-[0.12em] text-text-subtle">
        v2.1 · Hub
      </div>
    </aside>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-5 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">
      {children}
    </div>
  );
}

function SidebarLink({
  to,
  label,
  icon: Icon,
  color,
  nested,
}: Item & { nested?: boolean }) {
  return (
    <NavLink
      to={to}
      end={to === '/hub'}
      className={({ isActive }) =>
        `relative mx-2 flex items-center gap-2.5 rounded-md text-sm transition-colors ${
          nested ? 'pl-9 pr-3 py-1.5 text-[13px]' : 'px-3 py-2'
        } ${
          isActive
            ? 'bg-surface-2 text-text-strong font-medium'
            : 'text-text-muted hover:text-text hover:bg-surface-2/60'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && !nested && (
            // Left-rail accent — TYRL blue. Small, deliberate.
            <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-orange" />
          )}
          {/* Active state keeps the orange icon tint so the accent doubles as
              wayfinding; inactive items use the per-item pillar color from
              `color`, falling back to a muted neutral when none is set. */}
          <Icon
            size={nested ? 13 : 15}
            className={isActive ? 'text-orange' : color ?? 'text-text-subtle'}
          />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}
